import React, { useEffect } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { formatLastSync, useLastSync } from '../hooks/useLastSync';
import { useScope } from '../hooks/useScope';
import Breadcrumbs, { Crumb } from './Breadcrumbs';
import NavIcon, { NavIconName } from './NavIcon';
import { useLiveSessionCount } from './useLiveSessionCount';
import { Badge } from '../components/Badge';
import { NAV_SECTIONS, sectionForPath } from './sections';
import { useHover } from './useHover';
import { CONTENT_GUTTER, CONTENT_MAX_WIDTH, LEGACY_COLUMN_WIDTH, SIDEBAR_WIDTH } from './constants';
import { useDesktopScale } from './BreakpointProvider';
import { color } from '../theme';
import GlobalSearch from '../components/GlobalSearch';

// 'legacy': the page renders in today's centred 720px column (pages not converted yet).
// 'full': the page gets the whole content area and arranges itself with PageContainer / Grid / TwoPane.
// A number: like 'full', for a page centred in a narrower PageContainer of that maxWidth,
// so the breadcrumb row lines up with it.
export type LayoutWidth = 'legacy' | 'full' | number;

interface DesktopShellProps {
  children: React.ReactNode;
  width: LayoutWidth;
  // Trail below the section, ending with the current page: [{ label: 'הליכוד', to: '/parties/faction_1096' }, { label: 'עמית הלוי' }]
  breadcrumbs?: Crumb[];
}

/* ── Sidebar rows ──────────────────────────────────────────────────────── */

// Every destination is always listed, so rows never move as you navigate. Exactly one row
// is the page on screen ('page': tinted background plus a bar on its right edge); the rows
// above it in the tree are 'ancestor' (blue text only).
type RowState = 'page' | 'ancestor' | 'idle';

// Text of a sub-row starts where its group heading's text does (12px padding + 18px icon + 10px gap)
const SUB_INDENT = 28;

const rowStyle = (state: RowState, hovered: boolean, height: number, indent: number): React.CSSProperties => ({
  position: 'relative',
  display: 'flex', alignItems: 'center', gap: '10px',
  minHeight: height, padding: '0 12px', marginRight: indent, borderRadius: '8px',
  fontSize: '14px', textDecoration: 'none', minWidth: 0,
  // Hover is neutral grey, so it never reads as the blue "you are here" row
  backgroundColor: state === 'page' ? '#E6F1FB' : hovered ? '#EEF0F3' : 'transparent',
  color: state !== 'idle' ? '#185FA5' : hovered ? '#222' : '#444',
  fontWeight: state === 'idle' ? 400 : 600,
});

// The "you are here" bar, on the row's right (start) edge
const ActiveBar = () => (
  <span aria-hidden="true" style={{ position: 'absolute', right: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, backgroundColor: '#185FA5' }} />
);

interface NavRowProps {
  to: string;
  state: RowState;
  indent?: number;
  height?: number;
  // At the row's far (left) end, e.g. the live badge
  trailing?: React.ReactNode;
  children: React.ReactNode;
}

const NavRow = ({ to, state, indent = 0, height = 40, trailing, children }: NavRowProps) => {
  const [hovered, hoverHandlers] = useHover();
  return (
    <Link to={to} aria-current={state === 'page' ? 'page' : undefined} {...hoverHandlers} style={rowStyle(state, hovered, height, indent)}>
      {state === 'page' && <ActiveBar />}
      {children}
      {trailing && <span style={{ marginInlineStart: 'auto', display: 'flex' }}>{trailing}</span>}
    </Link>
  );
};

// Names a section whose pages are listed under it. A label, not a link: its pages are.
const GroupHeading = ({ id, icon, label, active }: { id: string; icon: NavIconName; label: string; active: boolean }) => (
  <div id={id} style={{ display: 'flex', alignItems: 'center', gap: '10px', height: 34, padding: '0 12px', fontSize: '13px', fontWeight: 600, color: active ? '#185FA5' : '#8a8a8a' }}>
    <NavIcon name={icon} />
    {label}
  </div>
);

// The detail page open now (e.g. an MK), nested under the list it came from
const CurrentItem = ({ label, indent }: { label: string; indent: number }) => (
  <span aria-current="page" style={{ ...rowStyle('page', false, 34, indent), fontSize: '13px' }}>
    <ActiveBar />
    {/* The ellipsis needs its own element: it doesn't apply to bare text in a flex row */}
    <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>{label}</span>
  </span>
);

/* ── Shell ─────────────────────────────────────────────────────────────── */

// Desktop (>= 1100px) app shell: a sidebar on the right with the six sections, and the
// page beside it. Phones and tablets never render this; they keep the mobile shell in Layout.
const DesktopShell = ({ children, width, breadcrumbs }: DesktopShellProps) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const search = new URLSearchParams(location.search);
  const section = sectionForPath(location.pathname);
  const lastSync = useLastSync();
  const scope = useScope();
  const scale = useDesktopScale();
  const liveCount = useLiveSessionCount();

  // A newly opened page starts at the top (as MobileShell does); back/forward (POP) keeps
  // the browser's position, and pages that save their own scroll still restore it after.
  useEffect(() => {
    if (navigationType === 'PUSH') window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  const current = breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : undefined;
  // Pages outside the six sections (e.g. /search) show their own crumbs alone
  const trail: Crumb[] | null = breadcrumbs && breadcrumbs.length > 0
    ? (section ? [{ label: section.label, to: section.to }, ...breadcrumbs] : breadcrumbs)
    : null;

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      direction: 'rtl',
      fontFamily: 'Heebo, sans-serif',
      // Same page background as MobileShell (theme token, owned by mobile-ux)
      backgroundColor: color.page,
    }}>

      {/* Sidebar — first in RTL flow, so it sits on the right */}
      <aside style={{
        width: `${SIDEBAR_WIDTH}px`, flexShrink: 0,
        position: 'sticky', top: 0, alignSelf: 'flex-start',
        // Viewport units scale with zoom too, so divide to keep it exactly one screen tall
        zoom: scale.sidebar,
        height: `calc(100vh / ${scale.sidebar})`, boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        padding: '18px 12px',
        backgroundColor: '#ffffff',
        borderLeft: '0.5px solid #e0e0e0',
        // The search results panel spills out over the page, so the sidebar doesn't clip
        // (only the nav scrolls) and stacks above the page's sticky panels
        zIndex: 100,
      }}>
        {/* Logo, and the Knesset it covers as plain text (a grey pill read as a button) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', padding: '0 8px 16px' }}>
          <Link to="/" aria-label="פוליטיקל — דף הבית" style={{ fontSize: '22px', fontWeight: 700, color: '#222', textDecoration: 'none' }}>
            פוליטי<span style={{ color: '#185FA5', fontSize: '26px' }}>קל</span>
          </Link>
          <span style={{ fontSize: '12px', color: '#888' }}>הכנסת ה-{scope.knesset}</span>
        </div>

        {/* Global search (mobile-ux's component): results open in a panel over the page */}
        <div style={{ padding: '0 0 14px' }}>
          {/* The default placeholder is cut off at sidebar width */}
          <GlobalSearch variant="dropdown" placeholder="חיפוש" />
        </div>

        {/* Fills the height and scrolls on a short screen; the 4px inset keeps focus rings unclipped */}
        <nav aria-label="ניווט ראשי" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minHeight: 0, overflowY: 'auto', padding: 4, margin: -4 }}>
          {NAV_SECTIONS.map(s => {
            const inSection = !!section && section.key === s.key;

            // A section with one page: a single link (a detail page opened from it nests under it)
            if (!s.subItems) {
              const live = s.key === 'schedule' && liveCount > 0
                ? <Badge tone="danger" dot={color.live}>{liveCount} בשידור</Badge>
                : undefined;
              return (
                <div key={s.key} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <NavRow to={s.to} state={inSection ? (current ? 'ancestor' : 'page') : 'idle'} trailing={live}>
                    <NavIcon name={s.icon} />
                    {s.label}
                  </NavRow>
                  {inSection && current && <CurrentItem label={current.label} indent={SUB_INDENT} />}
                </div>
              );
            }

            // A section with several pages: a heading, and its pages always listed under it
            const headingId = `nav-${s.key}`;
            return (
              <div key={s.key} role="group" aria-labelledby={headingId} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <GroupHeading id={headingId} icon={s.icon} label={s.label} active={inSection} />
                {s.subItems.map(sub => {
                  const subActive = inSection && sub.isActive(location.pathname, search);
                  return (
                    <React.Fragment key={sub.to}>
                      <NavRow to={sub.to} state={subActive ? (current ? 'ancestor' : 'page') : 'idle'} indent={SUB_INDENT} height={36}>
                        {sub.label}
                      </NavRow>
                      {subActive && current && <CurrentItem label={current.label} indent={SUB_INDENT + 12} />}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {lastSync && (
          <div style={{ borderTop: '0.5px solid #eee', padding: '12px 8px 0', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#888' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#3B7D23', flexShrink: 0 }} />
            {formatLastSync(lastSync)}
          </div>
        )}
      </aside>

      {/* Page */}
      <div style={{ flex: 1, minWidth: 0, zoom: scale.content }}>
        {trail && (
          <div style={{
            maxWidth: width === 'legacy' ? LEGACY_COLUMN_WIDTH : typeof width === 'number' ? width : CONTENT_MAX_WIDTH,
            margin: '0 auto',
            padding: width === 'legacy' ? '18px 16px 0' : `18px ${CONTENT_GUTTER}px 0`,
            boxSizing: 'border-box',
          }}>
            <Breadcrumbs items={trail} />
          </div>
        )}
        <main style={width === 'legacy'
          ? { maxWidth: `${LEGACY_COLUMN_WIDTH}px`, margin: '0 auto', paddingBottom: '48px' }
          : undefined}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default DesktopShell;
